import { i18n } from '~/utils';

import { Icon } from '@vestigas/ui/atoms';

import { type RedirectRouteType, type RouteType } from './types';

/**
 * Link names object for all routes in the application to be used in internal links.
 * Resolves a link name to its corresponding path.
 *
 * @example: linkTo.users => '/users'
 */
export const linkTo = {
  aiLabelEvaluation: '/ai/label-evaluation',
  aiModelEvaluation: '/ai/model-evaluation',
  aiProjects: '/ai/projects',
  apiPollerSecret: '/api-poller-secrets/:secretId',
  apiPollerSecrets: '/api-poller-secrets',
  companies: '/companies',
  companyAccounts: '/company-accounts',
  companyAliasAddresses: '/company-alias-addresses',
  connections: {
    dataExchangeCompanies: '/connections/data-exchange-companies',
    dataExchanges: '/connections/data-exchanges',
  },
  deliveryNotes: '/delivery-notes',
  endpoints: '/endpoints',
  ftpPoller: '/ftp-poller-secrets/:ftpPollerId',
  ftpPollers: '/ftp-poller-secrets',
  home: '/home',
  invoiceAiModel: '/invoice-ai-models/:invoiceAiModelSettingsId',
  invoiceAiModels: '/invoice-ai-models',
  invoiceForwardings: '/invoice-forwardings',
  invoiceStoppers: '/invoice-stoppers',
  pdfTemplates: '/pdf-templates',
  supplierRequests: '/supplier-requests/',
  tasks: {
    aiParserReview: '/ai-parser-review',
    assetReparsing: '/asset-reparsing',
    assetReparsingBackend: '/asset-reparsing-backend',
    deliveryNoteDeletion: '/delivery-note-deletion',
    deliveryNoteReparsingV2: '/delivery-note-reparsing-v2',
    invoiceReprocessingEdit: '/invoice-reprocessing/:processId',
    invoiceReprocessingList: '/invoice-reprocessing',
  },
  unitMappings: '/unit-mappings',
  users: '/users',
} as const;

export const NAVIGATION_ITEMS: Array<RouteType | RedirectRouteType> = [
  {
    children: [
      {
        icon: <Icon metaphor="aiExtraction" />,
        label: i18n.t(($) => $.routes.aiProjects.label, { ns: 'admin-webapp' }),
        name: 'aiProjects',
        path: linkTo.aiProjects,
      },
      {
        icon: <Icon metaphor="processWithAI" />,
        label: i18n.t(($) => $.routes.aiLabelEvaluation.label, {
          ns: 'admin-webapp',
        }),
        name: 'aiLabelEvaluation',
        path: linkTo.aiLabelEvaluation, // TODO
      },
      {
        icon: <Icon metaphor="processWithAI" />,
        label: i18n.t(($) => $.routes.aiModelEvaluation.label, {
          ns: 'admin-webapp',
        }),
        name: 'aiModelEvaluation',
        path: linkTo.aiModelEvaluation, // TODO
      },
    ],
    icon: <Icon metaphor="aiExtraction" />,
    label: i18n.t(($) => $.routes.aiProjects.label, { ns: 'admin-webapp' }),
    name: 'aiSection',
  },
  {
    icon: <Icon metaphor="processWithAI" />,
    label: i18n.t(($) => $.routes.aiParserReview.label, { ns: 'admin-webapp' }),
    name: 'aiParserReview',
    path: linkTo.tasks.aiParserReview,
  },
  {
    icon: <Icon metaphor="replay" />,
    label: i18n.t(($) => $.routes.assetReparsing.label, { ns: 'admin-webapp' }),
    name: 'assetReparsing',
    path: linkTo.tasks.assetReparsing,
  },
  {
    icon: <Icon metaphor="replay" />,
    label: i18n.t(($) => $.routes.assetReparsingBackend.label, {
      ns: 'admin-webapp',
    }),
    name: 'assetReparsingBackend',
    path: linkTo.tasks.assetReparsingBackend,
  },
  {
    icon: <Icon metaphor="replay" />,
    label: i18n.t(($) => $.routes.deliveryNoteReparsingV2.label, {
      ns: 'admin-webapp',
    }),
    name: 'deliveryNoteReparsingV2',
    path: linkTo.tasks.deliveryNoteReparsingV2,
  },
  {
    icon: <Icon metaphor="star" />,
    label: i18n.t(($) => $.routes.clients.label, { ns: 'admin-webapp' }),
    name: 'clients',
    path: linkTo.companyAccounts,
  },
  {
    icon: <Icon metaphor="company" />,
    label: i18n.t(($) => $.routes.companies.label, { ns: 'admin-webapp' }),
    name: 'companies',
    path: linkTo.companies,
  },
  {
    icon: <Icon metaphor="address" />,
    label: i18n.t(($) => $.routes.companyAliasAddresses.label, {
      ns: 'admin-webapp',
    }),
    name: 'companyAliasAddresses',
    path: linkTo.companyAliasAddresses,
  },
  {
    icon: <Icon metaphor="delivery" />,
    label: i18n.t(($) => $.routes.deliveryNotes.label, { ns: 'admin-webapp' }),
    name: 'deliveryNotes',
    path: linkTo.deliveryNotes,
  },
  {
    icon: <Icon metaphor="delete" />,
    label: i18n.t(($) => $.routes.deliveryNoteDeletion.label, {
      ns: 'admin-webapp',
    }),
    name: 'deliveryNoteDeletion',
    path: linkTo.tasks.deliveryNoteDeletion,
  },
  {
    icon: <Icon metaphor="endpoint" />,
    label: i18n.t(($) => $.routes.endpoints.label, { ns: 'admin-webapp' }),
    name: 'endpoints',
    path: linkTo.endpoints,
  },
  {
    icon: <Icon metaphor="storage" />,
    label: i18n.t(($) => $.routes.ftpPollers.label, { ns: 'admin-webapp' }),
    name: 'ftpPollers',
    path: linkTo.ftpPollers,
  },
  {
    icon: <Icon metaphor="storage" />,
    label: i18n.t(($) => $.routes.apiPollerSecrets.label, {
      ns: 'admin-webapp',
    }),
    name: 'apiPollerSecrets',
    path: linkTo.apiPollerSecrets,
  },
  {
    icon: <Icon metaphor="home" />,
    label: i18n.t(($) => $.routes.home.label, { ns: 'admin-webapp' }),
    name: 'home',
    path: linkTo.home,
  },
  {
    icon: <Icon metaphor="aiExtraction" />,
    label: i18n.t(($) => $.routes.invoiceAiModels.label, {
      ns: 'admin-webapp',
    }),
    name: 'invoiceAiModels',
    path: linkTo.invoiceAiModels,
  },
  {
    children: [
      {
        icon: <Icon metaphor="replay" />,
        label: i18n.t(($) => $.routes.invoiceReprocessingEdit.label, {
          ns: 'admin-webapp',
        }),
        name: 'invoiceReprocessingEdit',
        omitFromNav: true,
        path: '$id',
      },
    ],
    icon: <Icon className="rotate-90" metaphor="route" />,
    label: i18n.t(($) => $.routes.invoiceReprocessing.label, {
      ns: 'admin-webapp',
    }),
    name: 'invoiceReprocessingList',
    path: '/invoice-reprocessing',
  },
  {
    icon: <Icon className="rotate-90" metaphor="route" />,
    label: i18n.t(($) => $.routes.invoiceStoppers.label, {
      ns: 'admin-webapp',
    }),
    name: 'invoiceStoppers',
    path: '/invoice-stoppers',
  },
  {
    icon: <Icon metaphor="send" />,
    label: i18n.t(($) => $.routes.invoiceForwardings.label, {
      ns: 'admin-webapp',
    }),
    name: 'invoiceForwardings',
    path: linkTo.invoiceForwardings,
  },
  {
    icon: <Icon metaphor="pictureAsPdf" />,
    label: i18n.t(($) => $.routes.pdfTemplates.label, { ns: 'admin-webapp' }),
    name: 'pdfTemplates',
    path: linkTo.pdfTemplates,
  },
  {
    children: [
      {
        icon: <Icon metaphor="folderSpecial" />,
        label: i18n.t(
          ($) => $.routes.supplierConnectionsDataExchangeCompanies.label,
          {
            ns: 'admin-webapp',
          },
        ),
        name: 'supplierConnectionsDataExchangeCompanies',
        path: linkTo.connections.dataExchangeCompanies,
      },
      {
        icon: <Icon metaphor="dataExchange" />,
        label: i18n.t(($) => $.routes.supplierConnectionsDataExchanges.label, {
          ns: 'admin-webapp',
        }),
        name: 'supplierConnectionsDataExchanges',
        path: linkTo.connections.dataExchanges,
      },
    ],
    icon: <Icon metaphor="folderSpecial" />,
    label: i18n.t(($) => $.routes.supplierConnections.label, {
      ns: 'admin-webapp',
    }),
    name: 'supplierConnections',
    path: '/connections',
  },
  {
    icon: <Icon metaphor="company" />,
    label: i18n.t(($) => $.routes.supplierRequests.label, {
      ns: 'admin-webapp',
    }),
    name: 'supplierRequests',
    path: linkTo.supplierRequests,
  },
  {
    icon: <Icon className="rotate-90" metaphor="route" />,
    label: i18n.t(($) => $.routes.unitMappings.label, { ns: 'admin-webapp' }),
    name: 'unitMappings',
    path: linkTo.unitMappings,
  },
  {
    icon: <Icon metaphor="user" />,
    label: i18n.t(($) => $.routes.users.label, { ns: 'admin-webapp' }),
    name: 'users',
    path: linkTo.users,
  },
];
